import type {
  Lens,
  LensCard,
  LensCardConfig,
  LensCardPeriod,
  LensCardType,
  LensCardView,
  LensDataset,
  LensDatasetColumn,
  LensFilter,
  LensPlan,
  LensPlanCard,
  LensPlanSection,
  LensScope,
} from '~/types/leLens';
import { LENS_CARD_TYPE_OPTIONS } from '~/types/leLens';

import { uniqueId as uniqueFilterId } from './AddLensFilterDialog';
import { type ResolveCard, type ResolveDataset, resolveCard, scoreCandidate } from './cardResolve';
import { classifyColumnsByName, columnLabel, sentenceCase } from './columnClassification';
import { type CreateJourneyStart, WORKSPACE_BADGE_LABEL, type WorkspaceCandidate } from './createJourneyTypes';
import { inferRequestedCardType, makeUniqueCardId } from './generateLensCard';
import { workspaceCandidatesFromLenses } from './workspaceCandidates';

/** Shown on a row bound to an existing dataset. Reuses vocabulary the workspace-reuse pool
 *  already committed to: certification and blocked status both require the governed metric
 *  registry, out of scope here. */
export const NEW_QUERY_BADGE_LABEL = 'New query' as const;
/** Shown on an insight card: it has no SQL, but its AskRichard answer is
 * saved on the Lens and can be refreshed deliberately by an editor. */
export const NO_QUERY_BADGE_LABEL = 'Stored insight' as const;
/** Shown on a card an anchored plan already bound to a governed dataset: the server compiled it
 *  from the catalogue's own fields, so there is no query for this gate to write or share. */
export const GOVERNED_DATASET_BADGE_LABEL = 'Governed dataset' as const;

export type GateRowStatus = 'reused' | 'new' | 'insight';

/** One row in the plan review gate: one card, its status, and the one plain
 *  sentence explaining where its number comes from. */
export type GateRow = {
  id: string;
  title: string;
  type: LensCardType;
  /** Free text carried through for a 'new' row's build step; empty for a
   *  row picked straight from a dataset, since there was nothing to ask. */
  request: string;
  status: GateRowStatus;
  badge: string;
  sentence: string;
  chartLabel: string;
  /** The catalogue entry this row is bound to. Present only when status is
   *  'reused'. */
  candidateId?: string;
  /** The governed dataset id an anchored plan already bound this row to. Set only for a row an
   *  anchored plan resolved directly (LensPlanCard.datasetId), never alongside `candidateId`:
   *  that dataset is not one of the workspace's own reuse candidates, it is the Lens's own
   *  anchor, already accounted for outside this pool. */
  boundDatasetId?: string;
  /** How the shared dataset is cut for this card. Absent means the card
   *  reads every row as it stands, which is the table row's own behaviour. */
  view?: LensCardView;
  /** A configured analysis keeps its original window when appended to another board. */
  period?: LensCardPeriod;
  comparisonLabel?: string;
  config?: LensCardConfig;
  /** Explicit row edits leave the original configured Pulse presentation. */
  configuredTableEdited?: true;
  /** The shared query this 'new' row was grouped onto before the reader saw
   *  the plan. Rows carrying the same key are one query, not several. */
  plannedQueryKey?: string;
  /** How that shared query is cut for this card, kept apart from `view` so a
   *  grouping proposed at plan time cannot be read as a binding to a dataset
   *  that already exists. */
  plannedView?: LensCardView;
  /** The plan section this card belongs to. Absent when the plan came from a service that
   *  predates modes, and the gate then infers a group from the card itself as it always did. */
  sectionId?: string;
  /** That section's heading, which is what the gate groups and sorts by. */
  sectionTitle?: string;
  /** The analytical question this card answers, shown beneath its title at the gate. */
  question?: string;
};

export const SHARED_QUERY_BADGE_LABEL = 'Shared query' as const;

/**
 * Re-labels a row that grouping has put onto a query it shares with others.
 *
 * Left saying "New query", sixteen grouped rows each claim a query of their
 * own while the tally beneath them says two, and one of the two has to be
 * wrong. This is the row's side of that sentence.
 */
export function sharedRow(row: GateRow, datasetName: string, readers: number): GateRow {
  return {
    ...row,
    badge: SHARED_QUERY_BADGE_LABEL,
    sentence: `askRichard will write “${datasetName}” once and read this card from it, along with ${
      readers - 1 === 1 ? 'one other card' : `${readers - 1} other cards`
    }.`,
  };
}

/**
 * Moves one row to a new position in the plan.
 *
 * The plan's order is the order the board opens in, so a reader who wants the
 * headline numbers above the trends is arranging the dashboard here rather than
 * dragging sixteen cards around once it exists.
 */
export function moveGateRow(rows: ReadonlyArray<GateRow>, id: string, to: number): Array<GateRow> {
  const from = rows.findIndex((row) => row.id === id);
  if (from < 0 || to < 0 || to >= rows.length || to === from) {
    return [...rows];
  }
  const next = [...rows];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/** The reader-facing name for a card type, reusing the same labels the card
 *  type picker already shows rather than inventing a second set. */
export function cardTypeLabel(type: LensCardType): string {
  return LENS_CARD_TYPE_OPTIONS.find((option) => option.value === type)?.label ?? type;
}

/**
 * Every dataset on a dashboard the viewer can read, in the shape cardResolve
 * scores against, plus a lookup back to the original candidate so an
 * approved row can copy its real dataset definition into the new Lens.
 *
 * Every Lens dataset stores unaggregated rows; the grain a card asks for is
 * applied by the card's own grouping, not by the dataset (lensGrain.ts's own
 * comment: "A dataset returns days"), so every dataset here resolves at day
 * grain, the finest step in RESOLVE_GRAINS, until a dataset shape exists that
 * can declare a coarser one directly.
 *
 * `targetLensId`, when given, is the dashboard a plan is being appended onto:
 * its own datasets lead the pool, ahead of every other readable dashboard's,
 * because a card that joins ten others on the same query is the best reuse
 * outcome there is. Array.prototype.sort is a stable sort, so within "own"
 * and "other" nothing reorders beyond that one split.
 */
export function datasetPoolFromLenses(
  lenses: ReadonlyArray<Lens>,
  targetLensId?: string,
): {
  pool: Array<ResolveDataset>;
  candidatesById: ReadonlyMap<string, WorkspaceCandidate>;
} {
  const found = workspaceCandidatesFromLenses(lenses as Array<Lens>);
  const candidates = targetLensId
    ? [...found].sort((a, b) => Number(b.lensId === targetLensId) - Number(a.lensId === targetLensId))
    : found;
  const candidatesById = new Map(candidates.map((candidate) => [candidate.id, candidate]));
  const pool = candidates.map(
    (candidate): ResolveDataset => ({
      datasetId: candidate.id,
      datasetName: candidate.name,
      lensId: candidate.lensId,
      lensTitle: candidate.lensTitle,
      sourceTable: candidate.source,
      grain: 'day',
      columns: candidate.columns,
    }),
  );
  return { pool, candidatesById };
}

const NOTHING_TO_REUSE_SENTENCE = 'We’ll write and validate a query for this card before it is added.';

const INSIGHT_SENTENCE =
  'askRichard will save a written answer after this Lens is created. There is no query to write or reuse, and an editor can refresh the insight later.';

function newRow(card: Pick<LensPlanCard, 'title' | 'type' | 'request'>, id: string): GateRow {
  return {
    id,
    title: card.title,
    type: card.type,
    request: card.request,
    status: 'new',
    badge: NEW_QUERY_BADGE_LABEL,
    sentence: NOTHING_TO_REUSE_SENTENCE,
    chartLabel: cardTypeLabel(card.type),
  };
}

/** One card a dataset expansion proposes: enough to build both a GateRow and
 *  the LensCard it becomes on approval. */
export type ProposedCard = {
  title: string;
  type: LensCardType;
  /** Absent only for the closing table card, which reads the dataset as is. */
  view?: LensCardView;
};

/** Headline totals before a trend, a breakdown and a table start competing
 *  for room: a dataset with a dozen money-shaped columns still proposes a
 *  dashboard, not a wall of metric tiles. */
const MAX_METRIC_CARDS = 4;

/** Metric cards, plus at most one trend, one breakdown and the closing table. */
const MAX_PROPOSED_CARDS = MAX_METRIC_CARDS + 3;

/**
 * Turns one dataset's columns into the set of cards a shared query is meant
 * to produce: a metric per measure (money first, capped), a trend of the
 * primary measure over the date column, a breakdown of the primary measure
 * by the first dimension, and a table last. Pure and synchronous, no model
 * call and no network call, so proposing cards costs nothing beyond reading
 * the column names already on hand.
 *
 * Classification is name-based (see classifyColumnsByName): this call site
 * never has sample rows to check, only the column names the dataset
 * declares.
 *
 * A dataset with no measure at all cannot support a total, a trend or a
 * breakdown, so it proposes only the table.
 */
export function proposeCardsForColumns(columns: ReadonlyArray<string>): Array<ProposedCard> {
  const { measures, dateColumn, dimensions } = classifyColumnsByName(columns);

  if (!measures.length) {
    return [{ title: 'All rows', type: 'table' }];
  }

  const primary = measures[0];
  const cards: Array<ProposedCard> = measures.slice(0, MAX_METRIC_CARDS).map((measure) => ({
    title: `Total ${columnLabel(measure)}`,
    type: 'metric',
    view: { value: measure, aggregate: 'sum' },
  }));

  if (dateColumn) {
    cards.push({
      title: sentenceCase(`${columnLabel(primary)} over time`),
      type: 'line',
      view: { x: dateColumn, value: primary, aggregate: 'sum' },
    });
  }

  if (dimensions.length) {
    cards.push({
      title: sentenceCase(`${columnLabel(primary)} by ${columnLabel(dimensions[0])}`),
      type: 'bar',
      view: { x: dimensions[0], value: primary, aggregate: 'sum' },
    });
  }

  cards.push({ title: 'All rows', type: 'table' });

  return cards.slice(0, MAX_PROPOSED_CARDS);
}

/**
 * `proposeCardsForColumns`'s closing "All rows" card carries no view on
 * purpose: a 'sql' or 'catalogue' dataset's own query already returns every
 * declared row unaggregated, and `data.ts` reads that dataset's SQL directly
 * for a card with no view, never consulting it. A 'governed' dataset has no
 * such query of its own: the server compiles every card on it from the
 * catalogue's declared fields (`governed.ts`), with no "every row, ungrouped"
 * mode, only named-field aggregate queries, so the same viewless card is
 * refused by schema.ts (confirmed with B1, phase2/lens-contract.json's own
 * author: schema.ts:240 requires a view for exactly this dataset kind).
 *
 * This gives the closing card the same shape every other proposed card next
 * to it already uses (one dimension to group by, every measure summed),
 * which is the closest a table can get to "all rows" under that constraint.
 * When the dataset has no measure at all there is nothing to name: governed
 * datasets in that shape cannot produce a working table card today under any
 * view this client could attach, so the row is left viewless and will still
 * fail at Build, same as before this fix. That residual gap is a governed.ts
 * limitation, not something a client-side view can route around.
 */
function allRowsViewForGovernedDataset(columns: ReadonlyArray<string>): LensCardView | undefined {
  const { dimensions, measures } = classifyColumnsByName(columns);
  if (!measures.length) {
    return undefined;
  }
  const [primary, ...rest] = measures;
  return {
    aggregate: 'sum',
    ...(dimensions.length ? { x: dimensions[0] } : {}),
    value: primary,
    ...(rest.length ? { values: rest } : {}),
  };
}

/**
 * A dashboard filter for the geography term the scope confirmation block just resolved, so the
 * board actually narrows to a region rather than only naming one in a line of text under the
 * title. Confirming "region means destination region" is meaningless to the query unless the
 * canvas gains a control the BDM can set to their own patch; this mints that control the same
 * way a person adding one by hand would (`AddLensFilterDialog`'s own id scheme), left with no
 * default value, since the resolved *meaning* of "region" is not the same fact as *which* region
 * is the reader's.
 *
 * Refuses (returns `undefined`) unless the field is a declared dimension on one of the datasets
 * actually bound to this Lens: a scope can name a field the plan never anchored on (a routed
 * plan's `scope.dataset` can be null), and a filter naming a column no bound dataset returns
 * would silently never apply, which is worse than not offering one.
 */
export function scopeGeographyFilter(
  scope: LensScope | undefined,
  datasets: ReadonlyArray<LensDataset>,
  existingFilterIds: ReadonlyArray<string> = [],
  selectedValue?: string,
  /** Every value chosen, when there is more than one (01_CONTRACT_MULTI_ANSWER.md): the board
   *  region filter itself goes multi-select rather than only ever carrying the first. A single
   *  entry (or this being absent) falls back to `selectedValue`'s own single-value behaviour. */
  selectedValues?: ReadonlyArray<string>,
): LensFilter | undefined {
  const geography = scope?.geography;
  if (!geography) {
    return undefined;
  }
  const isDeclaredDimension = datasets.some((dataset) =>
    dataset.columns?.some((column) => column.name === geography.field && column.kind === 'dimension'),
  );
  if (!isDeclaredDimension) {
    return undefined;
  }
  const values = selectedValues && selectedValues.length > 0 ? selectedValues : selectedValue ? [selectedValue] : [];
  return {
    id: uniqueFilterId(geography.label, [...existingFilterIds]),
    label: geography.label,
    type: 'select',
    column: geography.field,
    ...(values.length > 1
      ? { defaultValue: [...values], multiple: true }
      : values.length === 1
        ? { defaultValue: values[0] }
        : {}),
  };
}

/**
 * Gate rows for the "Start from your work" lane: every candidate is already
 * the dataset the rows will read, so there is nothing left to resolve. Each
 * candidate expands into every card proposeCardsForColumns proposes for its
 * columns, so one shared dataset yields the several cards it is meant to,
 * not one table row per dataset. Every expanded row is still scored against
 * the dataset it came from, through the same scoreCandidate the brief lane
 * uses, so every lane explains a reused row in exactly the same words rather
 * than this lane inventing a second phrasing.
 *
 * `targetLensId`, when given, names the Lens being appended onto: a
 * candidate that already lives there is described as "this Lens" rather
 * than by its own title, since that is the case a reader most wants to see.
 */
export function gateRowsFromSelection(
  candidates: ReadonlyArray<WorkspaceCandidate>,
  targetLensId?: string,
): Array<GateRow> {
  return candidates.flatMap((candidate) => {
    const dataset: ResolveDataset = {
      datasetId: candidate.id,
      datasetName: candidate.name,
      lensId: candidate.lensId,
      lensTitle: candidate.lensTitle,
      sourceTable: candidate.source,
      grain: 'day',
      columns: candidate.columns,
    };
    const isGoverned = candidate.dataset.source?.kind === 'governed';
    const proposed = proposeCardsForColumns(candidate.columns).map((card) =>
      isGoverned && card.type === 'table' && !card.view
        ? { ...card, view: allRowsViewForGovernedDataset(candidate.columns) }
        : card,
    );
    const fallbackLocation = candidate.lensId === targetLensId ? 'this Lens' : `“${candidate.lensTitle}”`;

    return proposed.map((card, index): GateRow => {
      const requirement: ResolveCard = {
        title: card.title,
        type: card.type,
        request: '',
        sourceTable: dataset.sourceTable,
        grain: dataset.grain,
        columns: dataset.columns,
        targetLensId,
      };
      const match = scoreCandidate(requirement, dataset);
      return {
        id: `${candidate.id}::${index}`,
        title: card.title,
        type: card.type,
        request: '',
        status: 'reused',
        badge: WORKSPACE_BADGE_LABEL,
        sentence: match?.sentence ?? `Reuses the “${candidate.name}” dataset, already on ${fallbackLocation}.`,
        chartLabel: cardTypeLabel(card.type),
        candidateId: candidate.id,
        ...(card.view ? { view: card.view } : {}),
      };
    });
  });
}

/**
 * Gate rows for the "Describe what you need" lane, resolved locally against
 * the same dataset pool the your-work lane offers.
 *
 * A card with no `suggestedDatasetId` carries no structured ask for this
 * matcher to check a dataset against (source table, grain, columns), and
 * inventing one by parsing the free-text `request` would be exactly the
 * NLP-over-free-text guess cardResolve's own contract refuses to do. Such a
 * card is marked 'new' rather than resolved against nothing. When the model
 * did name a dataset, that dataset's own shape becomes the requirement, and
 * it is still scored against the whole pool rather than trusted outright, so
 * a better match can win and a bad suggestion still fails the same gates any
 * other candidate would.
 *
 * `targetLensId`, when given, is threaded onto every requirement so a
 * candidate already on that exact Lens reads as "this Lens" in its
 * row's sentence rather than by its own title.
 */
export function gateRowsFromPlan(
  cards: ReadonlyArray<LensPlanCard>,
  pool: ReadonlyArray<ResolveDataset>,
  targetLensId?: string,
  sections?: ReadonlyArray<LensPlanSection>,
): Array<GateRow> {
  const sectionById = new Map((sections ?? []).map((section) => [section.id, section]));
  // Stamped once over the finished rows rather than inside each of the four
  // branches below, which differ only in how the card binds to its data and
  // not at all in what the card is for.
  return gateRowsFrom(cards, pool, targetLensId).map((row, index) => {
    const card = cards[index];
    const section = card?.section ? sectionById.get(card.section) : undefined;
    if (!section) {
      return row;
    }
    return {
      ...row,
      sectionId: section.id,
      sectionTitle: section.title,
      ...(card.question ? { question: card.question } : {}),
    };
  });
}

function gateRowsFrom(
  cards: ReadonlyArray<LensPlanCard>,
  pool: ReadonlyArray<ResolveDataset>,
  targetLensId?: string,
): Array<GateRow> {
  return cards.map((card, index) => {
    const id = `plan-card-${index}`;

    if (card.type === 'insight') {
      return {
        id,
        title: card.title,
        type: card.type,
        request: card.request,
        status: 'insight',
        badge: NO_QUERY_BADGE_LABEL,
        sentence: INSIGHT_SENTENCE,
        chartLabel: cardTypeLabel(card.type),
      };
    }

    // An anchored plan already compiled this card's cut of the governed dataset server-side, so
    // there is nothing here to resolve against the reuse pool: it binds straight to the anchor.
    if (card.datasetId && card.view) {
      return {
        id,
        title: card.title,
        type: card.type,
        request: card.request,
        status: 'reused',
        badge: GOVERNED_DATASET_BADGE_LABEL,
        sentence: 'Reads the governed dataset directly: no query to write.',
        chartLabel: cardTypeLabel(card.type),
        view: card.view,
        boundDatasetId: card.datasetId,
      };
    }

    const suggested = card.suggestedDatasetId
      ? pool.find((dataset) => dataset.datasetId === card.suggestedDatasetId)
      : undefined;
    if (!suggested) {
      return newRow(card, id);
    }

    const requirement: ResolveCard = {
      title: card.title,
      type: card.type,
      request: card.request,
      sourceTable: suggested.sourceTable,
      grain: suggested.grain,
      columns: suggested.columns,
      suggestedDatasetId: card.suggestedDatasetId,
      targetLensId,
    };
    const best = resolveCard(requirement, pool).candidates[0];
    if (!best) {
      return newRow(card, id);
    }

    return {
      id,
      title: card.title,
      type: card.type,
      request: card.request,
      status: 'reused',
      badge: WORKSPACE_BADGE_LABEL,
      sentence: best.sentence,
      chartLabel: cardTypeLabel(card.type),
      candidateId: best.dataset.datasetId,
    };
  });
}

/**
 * One row for a card the reader typed into the gate's own "Add a card"
 * field, resolved through the exact two calls a proposed row from "Start
 * from your work" already runs: `proposeCardsForColumns` turns a dataset's
 * columns into the cards it can support, then `scoreCandidate` is the same
 * matcher every other row in the gate is bound through. No second matcher,
 * and no call to the planning endpoint for what is, after all, one card.
 *
 * A request is bound to the first dataset in `pool` whose own proposed cards
 * name a title matching it exactly (case- and whitespace-insensitive); `pool`
 * already sorts the dashboard being appended onto first, so a request that
 * dashboard's own dataset can answer binds there ahead of any other reader's.
 * A request matching nothing becomes a 'new' row exactly like a plan card
 * the model could not bind to anything: the same badge, the same sentence,
 * and a chart type guessed the same conservative way typing a request into
 * AskRichard's own ask box already guesses it, rather than parsing the
 * free-text request for columns it does not have.
 */
export function gateRowFromRequest(
  request: string,
  pool: ReadonlyArray<ResolveDataset>,
  targetLensId: string | undefined,
  id: string,
): GateRow {
  const wanted = request.trim().toLowerCase();

  for (const dataset of pool) {
    const proposed = proposeCardsForColumns(dataset.columns).find((card) => card.title.trim().toLowerCase() === wanted);
    if (!proposed) {
      continue;
    }

    const requirement: ResolveCard = {
      title: proposed.title,
      type: proposed.type,
      request: '',
      sourceTable: dataset.sourceTable,
      grain: dataset.grain,
      columns: dataset.columns,
      targetLensId,
    };
    const scored = scoreCandidate(requirement, dataset);
    if (!scored) {
      continue;
    }

    return {
      id,
      title: proposed.title,
      type: proposed.type,
      request: '',
      status: 'reused',
      badge: WORKSPACE_BADGE_LABEL,
      sentence: scored.sentence,
      chartLabel: cardTypeLabel(proposed.type),
      candidateId: dataset.datasetId,
      ...(proposed.view ? { view: proposed.view } : {}),
    };
  }

  return newRow({ title: request, type: inferRequestedCardType(request), request }, id);
}

/**
 * The only card types a plan row's own retype control ever offers: the exact
 * four proposeCardsForColumns and classifyColumnsByName already know how to
 * build a view for. Every other member of LENS_CARD_TYPES stays out of the
 * gate's reach, which is the boundary against the full card editor.
 */
export const GATE_RETYPE_TYPES = ['metric', 'line', 'bar', 'drivers', 'table'] as const;

/**
 * Every type a dataset's own columns can actually back, in the same terms
 * proposeCardsForColumns already decides them by: a metric needs one
 * measure, a line also needs a date column to plot along, a bar also needs a
 * dimension to group by, and a table needs nothing at all. Offering a type
 * this dataset cannot back would let the reader pick a chart that then fails
 * to render, so nothing here reaches further than what proposeCardsForColumns
 * itself already checks before proposing that same type.
 */
export function supportedRetypesForColumns(columns: ReadonlyArray<string>): Array<LensCardType> {
  const { measures, dateColumn, dimensions } = classifyColumnsByName(columns);
  const types: Array<LensCardType> = [];
  if (measures.length) {
    types.push('metric');
  }
  if (measures.length && dateColumn) {
    types.push('line');
  }
  if (measures.length && dimensions.length) {
    types.push('bar', 'drivers');
  }
  types.push('table');
  return types;
}

/** The same three-way split as classifyColumnsByName, read straight off a governed dataset's own
 *  declared `kind` per column rather than guessed from the column's name: a governed field always
 *  carries the real answer, so there is nothing here to infer. */
function classifyGovernedColumns(columns: ReadonlyArray<LensDatasetColumn>): {
  measures: Array<string>;
  dateColumn: string | undefined;
  dimensions: Array<string>;
} {
  return {
    measures: columns.filter((column) => column.kind === 'measure').map((column) => column.name),
    dateColumn: columns.find((column) => column.kind === 'date')?.name,
    dimensions: columns.filter((column) => column.kind === 'dimension').map((column) => column.name),
  };
}

/** supportedRetypesForColumns's governed twin: the same four-type vocabulary, gated on a governed
 *  dataset's own declared field kinds instead of a name guess. */
export function supportedRetypesForGovernedColumns(columns: ReadonlyArray<LensDatasetColumn>): Array<LensCardType> {
  const { measures, dateColumn, dimensions } = classifyGovernedColumns(columns);
  const types: Array<LensCardType> = [];
  if (measures.length) {
    types.push('metric');
  }
  if (measures.length && dateColumn) {
    types.push('line');
  }
  if (measures.length && dimensions.length) {
    types.push('bar', 'drivers');
  }
  types.push('table');
  return types;
}

/**
 * The types the gate's retype control offers for one row.
 *
 * A row bound to a governed dataset (`boundDatasetId`) is limited to whatever that dataset's own
 * declared fields can back, read from `boundDatasetsById`; a caller with nothing there for this
 * row's dataset (an anchor not yet fetched) falls to Table only, rather than guessing. A reused
 * workspace row is bound to a real dataset the same way, just scored by column name instead of a
 * declared kind, since a workspace dataset carries no such declaration. Its current type is always
 * kept in the list even on the rare chance it fell outside what the columns support, so the
 * control never renders a value absent from its own options. A new-query row has no dataset yet to
 * check a type against, so its type is only a brief for the authoring step that runs after
 * approval: it is offered the same four-type vocabulary with no column-based narrowing, since
 * narrowing would mean promising a shape nobody has confirmed the eventual query can produce. An
 * insight row runs no query and plots nothing, so the gate's own chart-type vocabulary does not
 * describe it at all; it offers no alternative, which is what keeps the control from rendering in
 * place of its chip.
 */
export function retypeOptionsForRow(
  row: GateRow,
  candidatesById: ReadonlyMap<string, WorkspaceCandidate>,
  boundDatasetsById?: ReadonlyMap<string, Pick<LensDataset, 'columns'>>,
): Array<LensCardType> {
  if (row.status === 'insight' || row.type === 'insight') {
    return [];
  }
  if (row.boundDatasetId) {
    const columns = boundDatasetsById?.get(row.boundDatasetId)?.columns ?? [];
    const supported = supportedRetypesForGovernedColumns(columns);
    return supported.includes(row.type) ? supported : [...supported, row.type];
  }
  if (row.status === 'new') {
    return [...GATE_RETYPE_TYPES];
  }
  const columns = row.candidateId ? (candidatesById.get(row.candidateId)?.columns ?? []) : [];
  const supported = supportedRetypesForColumns(columns);
  return supported.includes(row.type) ? supported : [...supported, row.type];
}

/**
 * The measures a reused row's own bound dataset actually returns, for the
 * remeasure control that lets the reader switch which one a metric, line or
 * bar row shows. Empty for anything else: a new-query row has no dataset yet
 * to read a column list from, so the gate says nothing about columns it
 * cannot see, and a table row shows every column as is rather than one
 * chosen measure.
 *
 * A row bound to a governed dataset reads the anchor's own declared measures the same way, rather
 * than the table-only fallback a missing `candidateId` used to leave it with. A row whose current
 * view is a declared ratio offers nothing to switch: a ratio is a fixed pair of two other measures,
 * not a single column this control can swap in place, the same rule that hides Combine for a ratio
 * measure everywhere else in the editor.
 */
export function measureColumnsForRow(
  row: GateRow,
  candidatesById: ReadonlyMap<string, WorkspaceCandidate>,
  boundDatasetsById?: ReadonlyMap<string, Pick<LensDataset, 'columns'>>,
): Array<string> {
  const view = row.view ?? row.plannedView;
  const selectedMeasures = [view?.value, ...(view?.values ?? [])].filter(
    (value, index, values): value is string => Boolean(value) && values.indexOf(value) === index,
  );
  // A multi-measure card is one deliberate comparison. Offering a single-value
  // selector beside it is misleading: it shows whichever dataset measure happens
  // to come first and implies the whole comparison can be edited one series at a time.
  // Multi-measure editing belongs in the full card editor, where the complete set
  // can be changed together.
  if (row.type === 'table' || view?.ratio || selectedMeasures.length > 1) {
    return [];
  }
  if (row.boundDatasetId) {
    const columns = boundDatasetsById?.get(row.boundDatasetId)?.columns ?? [];
    return classifyGovernedColumns(columns).measures;
  }
  if (row.status !== 'reused' || !row.candidateId) {
    return [];
  }
  const columns = candidatesById.get(row.candidateId)?.columns ?? [];
  return classifyColumnsByName(columns).measures;
}

/**
 * The dimensions a reused row's own bound dataset actually returns, for the
 * group-by control a bar row's remeasure edit offers. Only a bar row groups
 * by a column at all here, so every other type offers nothing to switch.
 *
 * A row bound to a governed dataset reads the anchor's own declared dimensions the same way,
 * rather than the table-only fallback a missing `candidateId` used to leave it with.
 */
export function dimensionColumnsForRow(
  row: GateRow,
  candidatesById: ReadonlyMap<string, WorkspaceCandidate>,
  boundDatasetsById?: ReadonlyMap<string, Pick<LensDataset, 'columns'>>,
): Array<string> {
  if (row.type !== 'bar') {
    return [];
  }
  if (row.boundDatasetId) {
    const columns = boundDatasetsById?.get(row.boundDatasetId)?.columns ?? [];
    return classifyGovernedColumns(columns).dimensions;
  }
  if (row.status !== 'reused' || !row.candidateId) {
    return [];
  }
  const columns = candidatesById.get(row.candidateId)?.columns ?? [];
  return classifyColumnsByName(columns).dimensions;
}

/**
 * The view a row's card should carry once its type changes, picked the same
 * way proposeCardsForColumns already picks a view for a fresh card of that
 * type: a metric needs only its measure, a line adds the date column to plot
 * along, a bar adds a dimension to group by, and a table reads the dataset as
 * it stands. The row's own current measure and group-by column are kept when
 * the new type can still use them, so switching a bar back to a line does not
 * forget which measure the reader had already picked.
 */
export function viewForRowType(
  type: LensCardType,
  currentView: LensCardView | undefined,
  columns: ReadonlyArray<string>,
): LensCardView | undefined {
  if (type === 'table') {
    return undefined;
  }
  const { measures, dateColumn, dimensions } = classifyColumnsByName(columns);
  const value = currentView?.value && measures.includes(currentView.value) ? currentView.value : measures[0];
  if (!value) {
    return undefined;
  }
  if (type === 'metric') {
    return { value, aggregate: 'sum' };
  }
  if (type === 'line') {
    return dateColumn ? { x: dateColumn, value, aggregate: 'sum' } : undefined;
  }
  if (type === 'bar' || type === 'drivers') {
    const x = currentView?.x && dimensions.includes(currentView.x) ? currentView.x : dimensions[0];
    return x ? { x, value, aggregate: 'sum' } : undefined;
  }
  return currentView;
}

/**
 * viewForRowType's governed twin, for a row bound to a catalogue dataset (`boundDatasetId`).
 *
 * Table is not "read the dataset as it stands" here the way it is for a reused workspace row: a
 * governed card has no raw, ungrouped query at all, the server always compiles one aggregate view,
 * so Table only changes how the same view renders. Wiping it on retype (viewForRowType's own
 * behaviour, correct for a workspace row) would leave a bound card with no measure and no ratio,
 * which the server refuses outright ("Choose a measure or a ratio to show."). Every other type
 * recomputes the view against the anchor's own declared fields, the same shape viewForRowType
 * already picks, and keeps a declared ratio intact across the switch when the fields behind it are
 * still there, since a ratio is not a column this function can resynthesise from a bare name.
 */
export function viewForBoundRowType(
  type: LensCardType,
  currentView: LensCardView | undefined,
  columns: ReadonlyArray<LensDatasetColumn>,
): LensCardView | undefined {
  if (type === 'table') {
    return currentView;
  }
  const { measures, dateColumn, dimensions } = classifyGovernedColumns(columns);
  if (currentView?.ratio && measures.includes(currentView.ratio.of) && measures.includes(currentView.ratio.per)) {
    if (type === 'metric') {
      return { ratio: currentView.ratio };
    }
    if (type === 'line') {
      return dateColumn ? { x: dateColumn, ratio: currentView.ratio } : undefined;
    }
    if (type === 'bar' || type === 'drivers') {
      const x = currentView.x && dimensions.includes(currentView.x) ? currentView.x : dimensions[0];
      return x ? { x, ratio: currentView.ratio } : undefined;
    }
  }
  const value = currentView?.value && measures.includes(currentView.value) ? currentView.value : measures[0];
  if (!value) {
    return undefined;
  }
  if (type === 'metric') {
    return { value, aggregate: 'sum' };
  }
  if (type === 'line') {
    return dateColumn ? { x: dateColumn, value, aggregate: 'sum' } : undefined;
  }
  if (type === 'bar' || type === 'drivers') {
    const x = currentView?.x && dimensions.includes(currentView.x) ? currentView.x : dimensions[0];
    return x ? { x, value, aggregate: 'sum' } : undefined;
  }
  return currentView;
}

/** Renames one row in place. The title is never load bearing: it does not
 *  touch what the row is bound to or what it will build, so this is the one
 *  edit that costs nothing beyond redrawing the row itself. */
export function renameGateRow(rows: ReadonlyArray<GateRow>, id: string, title: string): Array<GateRow> {
  return rows.map((row) => (row.id === id ? { ...row, title } : row));
}

function withoutConfiguredTablePresentation(row: GateRow): GateRow {
  return row.config?.tableColumns ? { ...row, config: undefined, configuredTableEdited: true } : row;
}

/**
 * Changes one row's card type in place. A reused row's view is recomputed
 * against its own bound dataset so the new type never renders with a shape
 * it cannot support; a new-query or insight row has no dataset to recompute
 * a view against, so only the type itself, and the label mirroring it,
 * change.
 */
export function retypeGateRow(
  rows: ReadonlyArray<GateRow>,
  id: string,
  type: LensCardType,
  candidatesById: ReadonlyMap<string, WorkspaceCandidate>,
  boundDatasetsById?: ReadonlyMap<string, Pick<LensDataset, 'columns'>>,
): Array<GateRow> {
  return rows.map((row) => {
    if (row.id !== id) {
      return row;
    }
    // A row bound to a governed dataset never falls to a fresh SQL row on retype, unlike a
    // workspace row below: retypeOptionsForRow only ever offers a type the anchor's own declared
    // fields already support, so there is never an unsupported choice here to fall back from.
    if (row.boundDatasetId) {
      const columns = boundDatasetsById?.get(row.boundDatasetId)?.columns ?? [];
      const edited = withoutConfiguredTablePresentation(row);
      const view = viewForBoundRowType(type, row.view, columns);
      return {
        ...edited,
        type,
        chartLabel: cardTypeLabel(type),
        view: edited.configuredTableEdited && view ? { ...view, where: row.view?.where } : view,
      };
    }
    if (row.status !== 'reused') {
      return { ...row, type, chartLabel: cardTypeLabel(type) };
    }
    const columns = row.candidateId ? (candidatesById.get(row.candidateId)?.columns ?? []) : [];
    const supported = supportedRetypesForColumns(columns);
    // A person can choose a different visual even when the dataset we found
    // first cannot support it. In that case the choice is a request for a
    // fresh, correctly-shaped query — never a broken chart with missing axes.
    if (!supported.includes(type)) {
      return {
        ...row,
        candidateId: undefined,
        chartLabel: cardTypeLabel(type),
        request: row.request || row.title,
        sentence: NOTHING_TO_REUSE_SENTENCE,
        status: 'new',
        badge: NEW_QUERY_BADGE_LABEL,
        type,
        view: undefined,
      };
    }
    return { ...row, type, chartLabel: cardTypeLabel(type), view: viewForRowType(type, row.view, columns) };
  });
}

/** Switches which measure a metric, line or bar row shows, keeping every
 *  other part of its view exactly as it was. */
export function remeasureGateRow(rows: ReadonlyArray<GateRow>, id: string, value: string): Array<GateRow> {
  return rows.map((row) => {
    if (row.id !== id) return row;
    const edited = withoutConfiguredTablePresentation(row);
    return {
      ...edited,
      view: { ...row.view, ...(edited.configuredTableEdited ? { values: undefined, ratio: undefined } : {}), value },
    };
  });
}

/** Switches which column a bar row groups by, keeping every other part of
 *  its view exactly as it was. */
export function regroupGateRow(rows: ReadonlyArray<GateRow>, id: string, x: string): Array<GateRow> {
  return rows.map((row) =>
    row.id === id ? { ...withoutConfiguredTablePresentation(row), view: { ...row.view, x } } : row,
  );
}

export type GateTally = { reused: number; newSql: number; insight: number };

export function tallyGateRows(rows: ReadonlyArray<GateRow>): GateTally {
  return rows.reduce<GateTally>(
    (tally, row) => {
      if (row.status === 'reused') {
        return { ...tally, reused: tally.reused + 1 };
      }
      if (row.status === 'new') {
        return { ...tally, newSql: tally.newSql + 1 };
      }
      return { ...tally, insight: tally.insight + 1 };
    },
    { reused: 0, newSql: 0, insight: 0 },
  );
}

function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * The number of distinct queries behind a set of gate rows, now that a row
 * is a card rather than a query: every reused row bound to the same
 * candidate shares that candidate's one query, and every 'new' row still
 * needs a query of its own written for it. An 'insight' row runs no query at
 * all, so it counts toward neither.
 */
export function distinctQueryCount(rows: ReadonlyArray<GateRow>): number {
  const reusedCandidateIds = new Set(
    rows
      .filter((row): row is GateRow & { candidateId: string } => row.status === 'reused' && Boolean(row.candidateId))
      .map((row) => row.candidateId),
  );
  // A row bound to a governed dataset shares nothing the way a reused workspace row can: the
  // server compiles each bound card into its own aggregate query at read time, so every one of
  // them counts here rather than silently vanishing from the total the way an unbound candidateId
  // used to (as many as "X cards from 0 queries", which is not honest about what running the
  // dashboard actually does).
  const boundRowCount = rows.filter((row) => Boolean(row.boundDatasetId)).length;
  return reusedCandidateIds.size + boundRowCount + newQueryCount(rows);
}

/** True once every row that runs a query at all reads a governed dataset directly: nothing here
 *  needs a query written or shared. An insight row runs no query either way, so it does not
 *  disqualify this on its own; an empty plan is not "all governed", it is nothing to say either
 *  way. */
export function allRowsGoverned(rows: ReadonlyArray<GateRow>): boolean {
  return rows.length > 0 && rows.every((row) => Boolean(row.boundDatasetId) || row.status === 'insight');
}

function joinWithAnd(items: ReadonlyArray<string>): string {
  if (items.length <= 1) {
    return items[0] ?? '';
  }
  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** Replaces the ordinary query-count sentence once every card reads a governed dataset directly:
 *  naming a query count there would still be true (distinctQueryCount already counts one per bound
 *  card) but reads as a queue of work when there is none to review or approve. */
export function governedPlanSentence(datasetNames: ReadonlyArray<string>): string {
  return `No query is written for any of these cards. Each one is a set of fields against ${joinWithAnd(datasetNames)}.`;
}

/** A 'new' row inside an otherwise-routed plan: the model could not answer it from any governed
 *  dataset, so it keeps the ordinary SQL-authoring path unchanged, but the row is worth a quiet
 *  note that it is not reading the governed data the rest of the plan reads, since sitting beside
 *  bound rows with no signal at all would read as an oversight rather than a deliberate fall-back. */
export function isQuietCustomQueryRow(row: GateRow, rows: ReadonlyArray<GateRow>): boolean {
  return !row.boundDatasetId && row.status === 'new' && rows.some((entry) => Boolean(entry.boundDatasetId));
}

/**
 * How many queries approving will actually run. A 'new' row grouped onto a
 * shared query counts once for its whole group, so a plan of sixteen cards
 * consolidated onto two datasets says two, not sixteen. A row with no key was
 * not grouped and still needs its own.
 */
export function newQueryCount(rows: ReadonlyArray<GateRow>): number {
  const newRows = rows.filter((row) => row.status === 'new');
  const keys = new Set(newRows.map((row) => row.plannedQueryKey).filter(Boolean));
  return keys.size + newRows.filter((row) => !row.plannedQueryKey).length;
}

/**
 * States the fact a shared dataset exists to demonstrate: several cards can
 * come from one query. Counting reused rows one-for-one with queries was
 * right back when a row was a query; now a row is a card, so this counts
 * cards and names the queries behind them instead, in the form
 * "6 cards from 1 query".
 */
export function cardsFromQueriesSentence(rows: ReadonlyArray<GateRow>): string {
  const queries = distinctQueryCount(rows);
  return `${pluralize(rows.length, 'card')} from ${pluralize(queries, 'query', 'queries')}`;
}

/** The quiet line beside the tally: what approving will actually run. */
export function newQueriesSentence(rows: ReadonlyArray<GateRow>): string {
  const queries = newQueryCount(rows);
  if (queries === 0) {
    return 'Nothing new to run';
  }
  return `${pluralize(queries, 'new query', 'new queries')} to run`;
}

/** A title for the new Lens when the journey did not already produce one:
 *  the brief lane gets one from the plan, and the your-work lane gets one
 *  named after what was picked, so the dashboard never opens as "Untitled". */
export function defaultDashboardTitle(start: CreateJourneyStart, plan: LensPlan | undefined): string {
  if (start.kind === 'brief') {
    const modelTitle = plan?.title?.trim();
    if (!modelTitle) {
      return 'New Lens';
    }
    const geographyLabel = plan?.scope?.geography?.label;
    // A model-written title can read as if a specific region were already applied ("Destination
    // Region Tracking") when only the field's meaning was resolved, not a value: the reader has
    // not yet chosen their own patch in the canvas filter this same approval mints
    // (scopeGeographyFilter). Saying so here matches the qualifier the canvas scope line's own
    // geography segment carries for the same reason.
    if (geographyLabel && modelTitle.toLowerCase().includes(geographyLabel.toLowerCase())) {
      return `${modelTitle} · choose your region`;
    }
    return modelTitle;
  }
  if (start.titleSeed?.trim()) {
    return start.titleSeed.trim();
  }
  if (start.candidates.length === 1) {
    return start.candidates[0].name;
  }
  return `${start.candidates.length} datasets from your work`;
}

export type ComposedPlan = {
  cards: Array<LensCard>;
  /** Stable plan-row identity for every immediately composed card. Titles
   *  are deliberately not used: two useful cards may share the same title. */
  rowIdByCardId: ReadonlyMap<string, string>;
  datasets: Array<LensDataset>;
  /** Rows that still need a query written and validated, after everything
   *  that could be bound to an existing dataset already has been. */
  newSqlRows: Array<GateRow>;
};

function uniqueDatasetId(base: string, taken: ReadonlySet<string>): string {
  if (!taken.has(base)) {
    return base;
  }
  let counter = 2;
  while (taken.has(`${base}-${counter}`)) {
    counter += 1;
  }
  return `${base}-${counter}`;
}

export type ComposeOptions = {
  /** Ids already spoken for on the target spec, so nothing minted here
   *  collides: cards, sections and datasets all share one id space. Empty
   *  for a brand-new Lens, since nothing is on it yet. */
  existingIds?: ReadonlyArray<string>;
  /** Set only when composing onto one specific existing dashboard. A row
   *  bound to a candidate already on that exact Lens is wired straight to
   *  its existing dataset id instead of being cloned under a second one,
   *  which is what keeps "10 cards, 1 dataset" true after the append rather
   *  than quietly becoming "11 cards, 2 datasets" running the same query. */
  ownLensId?: string;
};

/**
 * Turns the surviving gate rows into the spec a brand-new Lens is created
 * with, plus whatever still needs new SQL after approval. Also used to
 * compose an append onto an existing Lens (see `ComposeOptions`), in which
 * case the returned `datasets` are only the ones copied in from another
 * dashboard; a row bound to the target's own dataset carries that dataset's
 * existing id and is not duplicated into this list.
 *
 * Two datasets can be the very definition someone picked twice, on two
 * different rows: this dedupes by the catalogue entry a row is bound to, so
 * two cards reading the same reused dataset share one copy of it rather than
 * each getting their own.
 */
export function composeApprovedRows(
  rows: ReadonlyArray<GateRow>,
  candidatesById: ReadonlyMap<string, WorkspaceCandidate>,
  options: ComposeOptions = {},
): ComposedPlan {
  const { existingIds = [], ownLensId } = options;
  const cards: Array<LensCard> = [];
  const rowIdByCardId = new Map<string, string>();
  const datasets: Array<LensDataset> = [];
  const datasetIdByCandidateId = new Map<string, string>();
  const newSqlRows: Array<GateRow> = [];
  // Cards and copied-in datasets share one id space with whatever the target
  // spec already carries, so every id minted below is checked against the
  // same growing list rather than just against its own kind.
  const takenIds: Array<string> = [...existingIds];

  for (const row of rows) {
    const cardId = makeUniqueCardId(row.title || row.request || row.id, takenIds);
    takenIds.push(cardId);

    if (row.status === 'insight') {
      const card: LensCard = {
        id: cardId,
        type: 'insight',
        title: row.title,
        description: row.request,
        insightQuestion: row.request,
      };
      cards.push(card);
      rowIdByCardId.set(card.id, row.id);
      continue;
    }

    if (row.boundDatasetId) {
      // The anchor dataset itself is not composed here: it is added to the target spec once,
      // outside this loop, since it exists whether or not any anchored row is approved.
      const card: LensCard = {
        id: cardId,
        type: row.type,
        title: row.title,
        ...(row.request ? { description: row.request } : {}),
        ...(row.view ? { view: row.view } : {}),
        ...(row.period ? { period: row.period } : {}),
        ...(row.config ? { config: row.config } : {}),
        datasetId: row.boundDatasetId,
      };
      cards.push(card);
      rowIdByCardId.set(card.id, row.id);
      continue;
    }

    const candidate = row.status === 'reused' && row.candidateId ? candidatesById.get(row.candidateId) : undefined;
    if (!candidate) {
      // A 'new' row, or a 'reused' row whose candidate went missing between
      // planning and approval: either way there is nothing to bind to, so it
      // becomes a query AskRichard has to write rather than a card silently
      // pointing at nothing.
      newSqlRows.push(row);
      continue;
    }

    let datasetId = datasetIdByCandidateId.get(candidate.id);
    if (!datasetId) {
      if (ownLensId && candidate.lensId === ownLensId) {
        datasetId = candidate.datasetId;
      } else {
        datasetId = uniqueDatasetId(candidate.datasetId, new Set(takenIds));
        datasets.push({ ...candidate.dataset, id: datasetId });
      }
      datasetIdByCandidateId.set(candidate.id, datasetId);
      takenIds.push(datasetId);
    }

    const card: LensCard = {
      id: cardId,
      type: row.type,
      title: row.title,
      ...(row.request ? { description: row.request } : {}),
      ...(row.view ? { view: row.view } : {}),
      datasetId,
    };
    cards.push(card);
    rowIdByCardId.set(card.id, row.id);
  }

  return { cards, rowIdByCardId, datasets, newSqlRows };
}

/**
 * Where one approved row has got to while the plan is being built.
 *
 * These five have to be told apart on sight, not inferred from a single
 * progress bar: a reader who cannot see which card failed cannot retry only
 * that card, and a reader who cannot see which cards are already saved has to
 * assume none of them are. 'stopped' is the row a reader's own Stop reached
 * before it began, which is a different fact from a row that was tried and
 * failed.
 */
export type GateBuildStatus = 'pending' | 'building' | 'ready' | 'failed' | 'retrying' | 'stopped';

/** One row that could not be built, kept against its row id so a retry can
 *  re-run that row alone rather than the whole plan. */
export type GateRowFailure = { rowId: string; title: string; reason: string };

/**
 * What the reader is told when a build did not finish cleanly.
 *
 * Two facts, in this order, because they answer the two questions in that
 * order: what is already saved, and what is not. Saying only the second turns
 * a partly-finished dashboard into a total loss in the reader's head, which is
 * how they end up approving a second copy of work that is already there.
 */
export function buildOutcomeSentence({
  kept,
  failed,
  stopped,
  targetKind,
}: {
  kept: number;
  failed: number;
  stopped: number;
  targetKind: 'create' | 'append';
}): string {
  const where = targetKind === 'append' ? 'added to this Lens' : 'saved to the new Lens';
  const first = kept
    ? `${pluralize(kept, 'card')} ${kept === 1 ? 'was' : 'were'} ${where}.`
    : `No cards were ${where} yet.`;

  const rest: Array<string> = [];
  if (failed) {
    rest.push(
      `${pluralize(failed, 'card')} could not be built, and nothing was saved for ${failed === 1 ? 'it' : 'them'}.`,
    );
  }
  if (stopped) {
    rest.push(`${pluralize(stopped, 'card')} ${stopped === 1 ? 'was' : 'were'} not started.`);
  }
  return [first, ...rest].join(' ');
}

/** The retry action's own label, naming only what it will re-run. Offering
 *  "Try again" here would read as re-running the whole plan, which is exactly
 *  what must not happen once part of it is saved. */
export function retryFailedLabel(count: number): string {
  if (count === 1) {
    return 'Retry this card';
  }
  return `Retry ${count} cards`;
}

/**
 * What the reader is told when the planning call itself fails.
 *
 * The request layer's own message names a status code and echoes whatever the
 * server sent, and neither tells a person what happened. A planning failure
 * must never imply that a dashboard or partial plan was created.
 */
export function planFailureMessage(error: unknown): string {
  const raw = error instanceof Error ? error.message : '';
  const status =
    error &&
    typeof error === 'object' &&
    'status' in error &&
    typeof (error as { status?: unknown }).status === 'number'
      ? (error as { status: number }).status
      : undefined;
  const name = error && typeof error === 'object' ? (error as { name?: unknown }).name : undefined;
  if (status === 401 || name === 'AuthorizationError') {
    return 'Your sign-in has expired. Nothing has been created. Sign in again, then try again.';
  }
  if (
    (status !== undefined && status >= 400 && status < 500) ||
    (/status code 4\d\d/.test(raw) && /brief/i.test(raw))
  ) {
    return 'This Lens request could not be planned. Nothing has been created. Return to the Thread and try again with a more focused request.';
  }
  return 'askRichard could not prepare a Lens plan right now. Nothing has been created.';
}
